import { useCallback, useEffect, useState } from 'react';
import { projectId } from '../utils/supabase/info';
import type { ApplyPlan, ChangeOrderRecord, ChangeOrderSummary, TransitionRequest } from '../types/changeOrder';

export interface UseChangeOrdersArgs {
  engagementId: string | null | undefined;
  accessToken: string | null | undefined;
  enabled?: boolean;
}

export interface CreateChangeOrderInput {
  description: string;
  scopeChanges: string;
  notes?: string;
  changeOrderId?: string;
  suggestedConfigItems?: ChangeOrderRecord['suggestedConfigItems'];
  linkedScopeDeltas?: ChangeOrderRecord['linkedScopeDeltas'];
  /** The price: a fixed amount; `markupPercent` null or absent takes the SOW's markup. `null` takes the price off. */
  pricing?: { basePrice: number; markupPercent?: number | null } | null;
}

/** Whether the engagement's SOW is a fixed bid, and its markup (the server's `engagementMarkup`). */
export interface SowPricingInfo {
  fixedBid: boolean;
  markupPercent: number;
}

export interface UseChangeOrdersResult {
  changeOrders: ChangeOrderRecord[];
  summary: ChangeOrderSummary | null;
  companyId: string | null;
  canWrite: boolean;
  sowPricing: SowPricingInfo | null;
  loading: boolean;
  busy: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (input: CreateChangeOrderInput) => Promise<ChangeOrderRecord | null>;
  update: (coId: string, patch: Partial<CreateChangeOrderInput>) => Promise<ChangeOrderRecord | null>;
  transition: (coId: string, request: TransitionRequest) => Promise<{ changeOrder: ChangeOrderRecord; applyResult?: any; carried?: { carried: number } | null } | null>;
  applyPlan: (coId: string, acceptIds?: string[]) => Promise<ApplyPlan | null>;
}

function base(engagementId: string): string {
  return `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${encodeURIComponent(engagementId)}/change-orders`;
}

async function readError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.error ?? `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

/** Change orders for one engagement, with the PRD §4.4.1 transitions. */
export function useChangeOrders({ engagementId, accessToken, enabled = true }: UseChangeOrdersArgs): UseChangeOrdersResult {
  const [changeOrders, setChangeOrders] = useState<ChangeOrderRecord[]>([]);
  const [summary, setSummary] = useState<ChangeOrderSummary | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [sowPricing, setSowPricing] = useState<SowPricingInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }),
    [accessToken],
  );

  const refresh = useCallback(async () => {
    if (!engagementId || !accessToken || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(base(engagementId), { headers: headers() });
      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      setChangeOrders(Array.isArray(data.changeOrders) ? data.changeOrders : []);
      setSummary(data.summary ?? null);
      setCompanyId(data.companyId ?? null);
      setCanWrite(!!data.canWrite);
      setSowPricing(data.sowPricing ?? null);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load change orders');
    } finally {
      setLoading(false);
    }
  }, [engagementId, accessToken, enabled, headers]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      setBusy(true);
      setError(null);
      try {
        return await fn();
      } catch (e: any) {
        setError(e?.message ?? 'Request failed');
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const create = useCallback(
    (input: CreateChangeOrderInput) =>
      run(async () => {
        if (!engagementId) throw new Error('No engagement');
        const res = await fetch(base(engagementId), { method: 'POST', headers: headers(), body: JSON.stringify(input) });
        if (!res.ok) throw new Error(await readError(res));
        const data = await res.json();
        setChangeOrders(data.changeOrders ?? []);
        setSummary(data.summary ?? null);
        return data.changeOrder as ChangeOrderRecord;
      }),
    [engagementId, headers, run],
  );

  const update = useCallback(
    (coId: string, patch: Partial<CreateChangeOrderInput>) =>
      run(async () => {
        if (!engagementId) throw new Error('No engagement');
        const res = await fetch(`${base(engagementId)}/${encodeURIComponent(coId)}`, { method: 'PUT', headers: headers(), body: JSON.stringify(patch) });
        if (!res.ok) throw new Error(await readError(res));
        const data = await res.json();
        setChangeOrders(data.changeOrders ?? []);
        return data.changeOrder as ChangeOrderRecord;
      }),
    [engagementId, headers, run],
  );

  const transition = useCallback(
    (coId: string, request: TransitionRequest) =>
      run(async () => {
        if (!engagementId) throw new Error('No engagement');
        const res = await fetch(`${base(engagementId)}/${encodeURIComponent(coId)}/transition`, { method: 'POST', headers: headers(), body: JSON.stringify(request) });
        if (!res.ok) throw new Error(await readError(res));
        const data = await res.json();
        setChangeOrders(data.changeOrders ?? []);
        setSummary(data.summary ?? null);
        return { changeOrder: data.changeOrder as ChangeOrderRecord, applyResult: data.applyResult, carried: data.carried ?? null };
      }),
    [engagementId, headers, run],
  );

  const applyPlan = useCallback(
    (coId: string, acceptIds?: string[]) =>
      run(async () => {
        if (!engagementId) throw new Error('No engagement');
        const qs = acceptIds ? `?acceptIds=${encodeURIComponent(acceptIds.join(','))}` : '';
        const res = await fetch(`${base(engagementId)}/${encodeURIComponent(coId)}/apply-plan${qs}`, { headers: headers() });
        if (!res.ok) throw new Error(await readError(res));
        const data = await res.json();
        return data.plan as ApplyPlan;
      }),
    [engagementId, headers, run],
  );

  return { changeOrders, summary, companyId, canWrite, sowPricing, loading, busy, error, refresh, create, update, transition, applyPlan };
}
